'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { formatDT } from '@/lib/utils'
import { ArrowLeft, TrendingUp, TrendingDown, Package, Users, Wallet, Clock } from 'lucide-react'
import Link from 'next/link'

type Period = 'today' | 'week' | 'month'

export default function RapportPage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()
  const [period, setPeriod] = useState<Period>('month')
  const [s, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    if (session.user.role !== 'patron') { router.replace('/dashboard'); return }
    load()
  }, [session, period])

  function getRange() {
    const now = new Date()
    const d = (date: Date) => date.toISOString().split('T')[0]
    if (period === 'today') { const t = d(now); return { from: t, to: t } }
    if (period === 'week') { const w = new Date(now); w.setDate(w.getDate() - 6); return { from: d(w), to: d(now) } }
    return { from: d(new Date(now.getFullYear(), now.getMonth(), 1)), to: d(now) }
  }

  async function load() {
    setLoading(true)
    const { from, to } = getRange()
    setStats(await api.getRapport(session!.shop.id, from, to))
    setLoading(false)
  }

  const periodLabels: Record<Period, string> = { today: t("period_today"), week: '7 jours', month: 'Ce mois' }
  const maxBar = s?.ventes_par_jour?.reduce((m: number, d: any) => Math.max(m, parseFloat(d.ca_total)), 0) || 1

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">{t("rapport_title")}</h1>
        </div>
        <div className="flex px-4 pb-3 gap-2">
          {(['today', 'week', 'month'] as Period[]).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${period === p ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : s && (
        <div className="px-4 pt-4 space-y-4">

          {/* ── BÉNÉFICE NET (le résultat) ── */}
          <div className={`rounded-3xl p-5 text-white ${s.benefice_net >= 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
            <div className="flex items-center gap-1.5 mb-1">
              {s.benefice_net >= 0 ? <TrendingUp className="w-4 h-4 text-white/80" /> : <TrendingDown className="w-4 h-4 text-white/80" />}
              <p className="text-white/80 text-sm">{t("net_profit")}</p>
            </div>
            <p className="text-4xl font-bold">{s.benefice_net >= 0 ? '+' : ''}{formatDT(s.benefice_net)}</p>
            <p className="text-white/70 text-xs mt-1">{s.nb_ventes} vente{s.nb_ventes > 1 ? 's' : ''} · {t("on_goods_sold")}</p>
          </div>

          {/* ── CARTES RAPIDES ── */}
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { label: '{t("turnover")}', val: s.ca_total, cls: 'text-gray-900' },
              { label: '{t("gross_profit")}', val: s.benefice_brut, cls: 'text-indigo-700', prefix: '+' },
              { label: 'Charges', val: s.total_charges, cls: 'text-orange-600', prefix: '-' },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
                <p className="text-gray-400 text-[11px] mb-1">{c.label}</p>
                <p className={`text-base font-bold ${c.cls}`}>{c.prefix || ''}{formatDT(c.val)}</p>
              </div>
            ))}
          </div>

          {/* ── DÉTAIL P&L ── */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-800 mb-3">{t("result_label")} ({t("on_goods_sold")})</h3>
            <div className="space-y-0">
              {[
                { label: t("turnover"), val: formatDT(s.ca_total), cls: 'text-gray-700' },
                { label: '− Coût (ras mel)', val: '-' + formatDT(s.cout), cls: 'text-red-500' },
                { label: '= Bénéfice brut', val: '+' + formatDT(s.benefice_brut), cls: 'text-indigo-700 font-bold' },
                { label: '− Charges', val: '-' + formatDT(s.total_charges), cls: 'text-orange-500' },
              ].map(r => (
                <div key={r.label} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500 text-sm">{r.label}</span>
                  <span className={`font-semibold text-sm ${r.cls}`}>{r.val}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2.5">
                <span className="font-bold text-gray-800">= Bénéfice NET</span>
                <span className={`font-bold text-lg ${s.benefice_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {s.benefice_net >= 0 ? '+' : ''}{formatDT(s.benefice_net)}
                </span>
              </div>
            </div>
          </div>

          {/* ── TRÉSORERIE (cash réel) ── */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-5 h-5 text-green-500" />
              <h3 className="font-bold text-gray-800">{t("treasury")}</h3>
            </div>
            <div className="space-y-0">
              <div className="flex justify-between py-2 border-b border-gray-50">
                <span className="text-gray-500 text-sm">{t("encaisse_on_sales")}</span>
                <span className="font-semibold text-sm text-green-600">+{formatDT(s.encaisse)}</span>
              </div>
              {s.paiements_clients > 0 && (
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500 text-sm">{t("client_payments_received")}</span>
                  <span className="font-semibold text-sm text-teal-600">+{formatDT(s.paiements_clients)}</span>
                </div>
              )}
              {s.avances_clients > 0 && (
                <div className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500 text-sm">Avances reçues</span>
                  <span className="font-semibold text-sm text-teal-600">+{formatDT(s.avances_clients)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2.5">
                <span className="font-bold text-gray-800">{t("total_cash_in")}</span>
                <span className="font-bold text-lg text-green-600">+{formatDT(s.cash_encaisse_total)}</span>
              </div>
            </div>

            {/* Kardé créé cette période */}
            {s.karde_cree > 0 && (
              <div className="mt-3 bg-orange-50 border border-orange-200 rounded-2xl p-3 flex items-center gap-3">
                <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <div className="flex-1 text-sm">
                  <p className="font-bold text-orange-700">{formatDT(s.karde_cree)} en kardé</p>
                  <p className="text-orange-500 text-xs">Vendu mais pas encore encaissé (inclus dans le CA, pas dans le cash)</p>
                </div>
              </div>
            )}
          </div>

          {/* ── KARDÉ EN COURS (créances) ── */}
          {s.karde_en_cours?.total_dette > 0 && (
            <Link href="/clients" className="block bg-white rounded-2xl p-4 shadow-sm border border-orange-100 active:scale-[0.99]">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-orange-500" />
                <h3 className="font-bold text-gray-800">Kardé en cours (total)</h3>
                <span className="ml-auto text-xs text-indigo-500 font-semibold">Voir clients →</span>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold text-orange-600">{formatDT(s.karde_en_cours.total_dette)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{s.karde_en_cours.nb_clients} client{s.karde_en_cours.nb_clients > 1 ? 's' : ''} à recouvrer</p>
                </div>
              </div>
            </Link>
          )}

          {/* ── GRAPHIQUE ── */}
          {s.ventes_par_jour?.length > 0 && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-4">Ventes par jour</h3>
              <div className="flex items-end gap-1.5 h-28">
                {s.ventes_par_jour.map((d: any) => {
                  const hCA = Math.max(4, (parseFloat(d.ca_total) / maxBar) * 104)
                  const hEnc = Math.max(2, (parseFloat(d.encaisse) / maxBar) * 104)
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="w-full relative" style={{ height: '104px' }}>
                        <div className="absolute bottom-0 w-full bg-indigo-100 rounded-t-md" style={{ height: `${hCA}px` }} />
                        <div className="absolute bottom-0 w-full bg-indigo-500 rounded-t-md" style={{ height: `${hEnc}px` }} />
                      </div>
                      <span className="text-[9px] text-gray-400 truncate w-full text-center">
                        {new Date(d.date + 'T12:00:00').toLocaleDateString('fr', { day: '2-digit', month: '2-digit' })}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="flex gap-4 mt-3 justify-center">
                <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-indigo-100" /> CA total</span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-indigo-500" /> Encaissé</span>
              </div>
            </div>
          )}

          {/* ── TOP ARTICLES ── */}
          {s.top_articles?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <Package className="w-5 h-5 text-indigo-500" />
                <h3 className="font-bold text-gray-800">{t("top_articles")}</h3>
              </div>
              {s.top_articles.map((a: any, i: number) => (
                <div key={a.name} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-600'}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{a.name}</p>
                    <p className="text-xs text-gray-400">{a.qty} {t("sold_count")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-indigo-700 text-sm">{formatDT(parseFloat(a.ca))}</p>
                    <p className="text-xs text-green-600">+{formatDT(parseFloat(a.benefice))}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      )}
    </div>
  )
}
